package org.dronda.app.web.ui.eventcreate

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import app.cash.molecule.RecompositionMode
import app.cash.molecule.launchMolecule
import com.github.michaelbull.result.Err
import com.github.michaelbull.result.Result
import com.github.michaelbull.result.getOrElse
import com.github.michaelbull.result.map
import com.github.michaelbull.result.mapError
import com.github.michaelbull.result.onErr
import com.github.michaelbull.result.onOk
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import org.dronda.api.wishlist.EventLimits
import org.dronda.api.wishlist.toApi
import org.dronda.service.wishlist.core.domain.EventMode
import org.dronda.api.wishlist.EventRequest
import org.dronda.api.wishlist.ExclusionEmails
import org.dronda.app.web.repository.EventRepository
import org.dronda.app.web.ui.components.ExclusionLabel
import org.dronda.app.web.ui.components.SelectOption
import org.dronda.app.web.ui.util.collectEventSink
import org.dronda.app.web.ui.util.exclusionDirectionChoices
import org.dronda.app.web.ui.util.exclusionLabel
import org.dronda.service.wishlist.core.domain.SantaExclusionDirection

class CreateEventViewModel(
    private val creatorEmail: String,
    private val eventRepository: EventRepository,
) {
    private val scope = CoroutineScope(Dispatchers.Main)
    private val eventSink = MutableSharedFlow<Event>(extraBufferCapacity = 8)

    val uiState: StateFlow<UiState> = scope.launchMolecule(mode = RecompositionMode.Immediate) {
        var step by remember { mutableStateOf(Step.Basics) }
        var name by remember { mutableStateOf("") }
        var date by remember { mutableStateOf("") }
        var mode by remember { mutableStateOf(EventMode.SECRET_SANTA) }
        var budget by remember { mutableStateOf("") }
        var emailDraft by remember { mutableStateOf("") }
        var emails by remember { mutableStateOf(listOf<String>()) }
        var exclusionA by remember { mutableStateOf<String?>(null) }
        var exclusionB by remember { mutableStateOf<String?>(null) }
        var exclusionDirection by remember { mutableStateOf(SantaExclusionDirection.BOTH) }
        var exclusions by remember { mutableStateOf(listOf<ExclusionDraft>()) }
        var showErrors by remember { mutableStateOf(false) }
        var emailError by remember { mutableStateOf<String?>(null) }
        var submitting by remember { mutableStateOf(false) }
        var submitError by remember { mutableStateOf<String?>(null) }
        var created by remember { mutableStateOf<Created?>(null) }

        // Derived values for rendering only. The event handler below is captured once, so it must read the
        // `by remember` state directly instead of these vals.
        val steps = remember(mode) { stepsFor(mode) }
        val people = remember(emails) { listOf(creatorEmail) + emails }
        val basicsErrors = remember(name, date, budget) { validateBasics(name, date, budget) }

        collectEventSink(eventSink) { event ->
            when (event) {
                is Event.NameChanged -> name = event.value
                is Event.DateChanged -> date = event.value
                is Event.ModeChanged -> mode = event.mode
                is Event.BudgetChanged -> budget = event.value
                is Event.EmailDraftChanged -> {
                    emailDraft = event.value
                    emailError = null
                }
                Event.AddEmails -> {
                    val result = addEmails(emailDraft, listOf(creatorEmail) + emails)
                    emails = emails + result.added
                    emailError = result.error
                    if (result.error == null) emailDraft = ""
                }
                is Event.RemoveEmail -> {
                    emails = emails - event.email
                    exclusions = exclusions.filterNot { it.involves(event.email) }
                }
                is Event.ExclusionAChanged -> exclusionA = event.email
                is Event.ExclusionBChanged -> exclusionB = event.email
                is Event.ExclusionDirectionChanged -> exclusionDirection = event.direction
                Event.AddExclusion -> {
                    val a = exclusionA
                    val b = exclusionB
                    if (a != null && b != null && a != b && exclusions.none { it.matches(a, b) }) {
                        exclusions = exclusions + ExclusionDraft(a, b, exclusionDirection)
                        exclusionA = null
                        exclusionB = null
                        exclusionDirection = SantaExclusionDirection.BOTH
                    }
                }
                is Event.RemoveExclusion -> exclusions = exclusions.filterIndexed { index, _ -> index != event.index }
                Event.Next -> {
                    val currentSteps = stepsFor(mode)
                    if (step == Step.Basics && validateBasics(name, date, budget).hasErrors) {
                        showErrors = true
                    } else {
                        showErrors = false
                        step = currentSteps[(currentSteps.indexOf(step) + 1).coerceAtMost(currentSteps.lastIndex)]
                    }
                }
                Event.Back -> {
                    val currentSteps = stepsFor(mode)
                    step = currentSteps[(currentSteps.indexOf(step) - 1).coerceAtLeast(0)]
                }
                Event.Submit -> scope.launch {
                    submitting = true
                    submitError = null
                    try {
                        submit(name, date, mode, budget, emails, if (mode == EventMode.SECRET_SANTA) exclusions else emptyList())
                            .onOk { created = it }
                            .onErr { submitError = it }
                    } finally {
                        submitting = false
                    }
                }
            }
        }

        UiState(
            step = step,
            stepNumber = steps.indexOf(step) + 1,
            stepCount = steps.size,
            isLastStep = step == steps.last(),
            name = name,
            date = date,
            mode = mode,
            budget = budget,
            nameError = basicsErrors.name.takeIf { showErrors },
            dateError = basicsErrors.date.takeIf { showErrors },
            budgetError = basicsErrors.budget.takeIf { showErrors },
            emailDraft = emailDraft,
            emailError = emailError,
            peopleCountLabel = "${people.size} / ${EventLimits.MAX_PARTICIPANTS}",
            participants = remember(people) {
                people.map { ParticipantRow(email = it, isCreator = it == creatorEmail) }
            },
            canAddPeople = people.size < EventLimits.MAX_PARTICIPANTS,
            exclusionOptions = remember(people) { people.map { SelectOption(value = it, label = it) } },
            exclusionA = exclusionA,
            exclusionB = exclusionB,
            exclusionDirection = exclusionDirection,
            directionChoices = exclusionDirectionChoices,
            canAddExclusion = exclusionA != null && exclusionB != null && exclusionA != exclusionB,
            exclusions = remember(exclusions) { exclusions.map { exclusionLabel(it.a, it.b, it.direction) } },
            warnings = remember(mode, people, exclusions) { santaWarnings(mode, people, exclusions) },
            review = remember(name, date, mode, budget, people, exclusions) {
                reviewLines(name, date, mode, budget, people.size, exclusions.size)
            },
            submitting = submitting,
            submitError = submitError,
            created = created,
        )
    }

    fun take(event: Event) {
        eventSink.tryEmit(event)
    }

    private suspend fun submit(
        name: String,
        date: String,
        mode: EventMode,
        budget: String,
        emails: List<String>,
        exclusions: List<ExclusionDraft>,
    ): Result<Created, String> {
        val request = EventRequest.from(
            name = name.trim(),
            date = date,
            groupId = null,
            mode = mode.toApi(),
            budget = budget.trim().takeIf { mode == EventMode.SECRET_SANTA && it.isNotEmpty() },
            participantEmails = emails,
            exclusions = exclusions.map { ExclusionEmails(it.a, it.b, it.direction.toApi()) },
        ).getOrElse { return Err("Please check the event details and try again.") }

        return eventRepository.createEvent(request)
            .map { Created(it.id.toString(), it.unknownEmails) }
            .mapError { "Failed to create event. Please try again." }
    }

    enum class Step(val title: String) {
        Basics("Basics"),
        People("People"),
        Exclusions("Exclusions"),
        Review("Review"),
    }

    data class UiState(
        val step: Step,
        val stepNumber: Int,
        val stepCount: Int,
        val isLastStep: Boolean,
        val name: String,
        val date: String,
        val mode: EventMode,
        val budget: String,
        val nameError: String?,
        val dateError: String?,
        val budgetError: String?,
        val emailDraft: String,
        val emailError: String?,
        val peopleCountLabel: String,
        val participants: List<ParticipantRow>,
        val canAddPeople: Boolean,
        val exclusionOptions: List<SelectOption>,
        val exclusionA: String?,
        val exclusionB: String?,
        val exclusionDirection: SantaExclusionDirection,
        val directionChoices: List<SelectOption>,
        val canAddExclusion: Boolean,
        val exclusions: List<ExclusionLabel>,
        val warnings: List<String>,
        val review: List<String>,
        val submitting: Boolean,
        val submitError: String?,
        val created: Created?,
    ) {
        val isSecretSanta: Boolean get() = mode == EventMode.SECRET_SANTA
    }

    data class ParticipantRow(val email: String, val isCreator: Boolean)

    data class Created(val eventId: String, val unknownEmails: List<String>)

    sealed interface Event {
        data class NameChanged(val value: String) : Event
        data class DateChanged(val value: String) : Event
        data class ModeChanged(val mode: EventMode) : Event
        data class BudgetChanged(val value: String) : Event
        data class EmailDraftChanged(val value: String) : Event
        data object AddEmails : Event
        data class RemoveEmail(val email: String) : Event
        data class ExclusionAChanged(val email: String?) : Event
        data class ExclusionBChanged(val email: String?) : Event
        data class ExclusionDirectionChanged(val direction: SantaExclusionDirection) : Event
        data object AddExclusion : Event
        data class RemoveExclusion(val index: Int) : Event
        data object Next : Event
        data object Back : Event
        data object Submit : Event
    }
}
